import { createElement, useEffect } from 'react';

import clsx from 'clsx';
import { FormState, UseFormSetValue } from 'react-hook-form';

import useGa from '@analytics/hooks/useGa';
import TutorialLegend from '@event/components/TutorialModal';
import useRandomizedContent from '@event/hooks/useRandomizedContent';
import useEventTypesStore from '@event/store/useEventTypesStore';
import { useTutorialStore } from '@event/store/useTutorialStore';
import { CreateEvent, EventType } from '@event/types';
import Button from '@ui/components/Button';
import IconQuestionMark from '@ui/components/icons/IconQuestionMark';
import useClientStore from '@utils/hooks/useClientStore';

interface TypeEventSelectionProps {
  setValue: UseFormSetValue<CreateEvent>;
  formState: FormState<CreateEvent>;
  isTutorialActive?: boolean;
  isTutorialEventType?: boolean;
}

const TypeEventSelection = ({
  setValue,
  formState,
  isTutorialActive = false,
  isTutorialEventType = false,
}: TypeEventSelectionProps) => {
  const { eventsType, selectedEventType, setEventsType, setSelectedEventType } =
    useEventTypesStore();
  const { eventTitle, eventDescription } = useRandomizedContent(
    selectedEventType,
    formState.isDirty,
  );
  const logToGa = useGa();

  useEffect(() => {
    setValue('description', eventDescription);
    setValue('name', eventTitle);
  }, [eventDescription, eventTitle, setValue]);

  const tutorialStore = useClientStore(useTutorialStore, (state) => state);

  const handleSelectEventType = (eventType: EventType) => {
    const updatedSelectedEventType = eventType.isSelected
      ? null
      : { ...eventType, isSelected: true };
    setSelectedEventType(updatedSelectedEventType);

    const updatedEventsType = eventsType.map((eventTypeItem) =>
      eventTypeItem.id === updatedSelectedEventType?.id
        ? updatedSelectedEventType
        : { ...eventTypeItem, isSelected: false },
    );

    setEventsType(updatedEventsType);

    logToGa({
      event: 'button_click',
      button: {
        text: eventType.name,
        type: 'event type',
        phase: 'planning',
      },
    });
  };

  return (
    <div className="mb-4 flex lg:mb-8">
      <div
        className={clsx(
          'flex gap-2 overflow-x-auto p-1 scrollbar-hide lg:flex-wrap lg:overflow-x-hidden',
          !isTutorialActive && 'mr-4',
        )}
      >
        {isTutorialEventType && (
          <TutorialLegend
            textBubble="Vybereme vám obrázky a popisky podle události. Můžete je kdykoliv změnit."
            textHeadingCounter="1/5"
            textHeading="Vyber si typ události."
            onSubmit={() => {
              tutorialStore?.goToNextStep();
            }}
            className="left-0 top-[76px] md:left-[18px] lg:left-[32px] lg:top-[92px]"
          />
        )}
        {eventsType.map((eventType, index) => (
          <Button
            disabled={eventType.isSelected}
            isActive={eventType.isSelected}
            key={index}
            type="pill"
            addonBefore={createElement(eventType.iconComponent, {
              className: clsx(
                'mr-[10px]',
                eventType.isSelected && 'fill-brand-primary-purple',
              ),
            })}
            onClick={() => {
              handleSelectEventType(eventType);

              if (isTutorialActive) {
                tutorialStore?.goToNextStep();
              }
            }}
          >
            {eventType.name}
          </Button>
        ))}
      </div>
      {!isTutorialActive && (
        <Button
          type="pill"
          onClick={() => {
            tutorialStore?.setTutorialStep('eventType', true);
          }}
          addonBefore={<IconQuestionMark />}
          classNameWrapper="ml-auto m-1"
        />
      )}
    </div>
  );
};

export default TypeEventSelection;
